import clsx from 'clsx';
import {
  ChevronDown,
  ChevronRight,
  Copy,
  Indent,
  Lock,
  MoveDown,
  MoveUp,
  Outdent,
  Plus,
  Trash2,
} from 'lucide-react';
import { useState } from 'react';
import { flattenSections, type DocumentContent, type Section } from '@imsenta/shared';

export interface SectionTreeProps {
  content: DocumentContent;
  activeId: string | null;
  onSelect: (id: string) => void;
  onAdd: (afterId: string | null) => void;
  onAddChild: (parentId: string) => void;
  onMove: (id: string, direction: -1 | 1) => void;
  onIndent: (id: string) => void;
  onOutdent: (id: string) => void;
  onDuplicate: (id: string) => void;
  onRemove: (id: string) => void;
  readOnly?: boolean;
}

/** The document outline: reorder, nest and add sections without touching text. */

/**
 * Where a section sits among its own siblings.
 *
 * Moving and indenting act on siblings, so the buttons have to be enabled from
 * sibling position — using the flattened index offered moves that silently did
 * nothing.
 */
function siblingPositions(content: DocumentContent): Map<string, { index: number; count: number; depth: number }> {
  const map = new Map<string, { index: number; count: number; depth: number }>();
  const walk = (sections: Section[], depth: number) => {
    sections.forEach((section, index) => {
      map.set(section.id, { index, count: sections.length, depth });
      walk(section.children, depth + 1);
    });
  };
  walk(content.sections, 1);
  return map;
}

export function SectionTree({
  content,
  activeId,
  onSelect,
  onAdd,
  onAddChild,
  onMove,
  onIndent,
  onOutdent,
  onDuplicate,
  onRemove,
  readOnly,
}: SectionTreeProps) {
  /*
   * On a wide screen the outline is a permanent sidebar. On a phone it sits
   * above the section being edited, so it starts collapsed — otherwise a long
   * document pushes the editor off the first screen.
   */
  const [expanded, setExpanded] = useState(() => (typeof window === 'undefined' ? true : window.innerWidth >= 1024));
  const entries = flattenSections(content, { includeUnnumbered: true });
  const activeTitle = entries.find((e) => e.section.id === activeId)?.section.title ?? null;
  const positions = siblingPositions(content);
  const MAX_DEPTH = 5;

  return (
    <div className="flex h-full flex-col">
      <div className="flex items-center justify-between px-3 py-2">
        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          className="flex items-center gap-1 text-[11px] font-semibold tracking-wider text-ink-500 uppercase hover:text-ink-800"
        >
          {expanded ? <ChevronDown className="size-3.5" /> : <ChevronRight className="size-3.5" />}
          Outline
          <span className="ml-1 font-normal normal-case">({entries.length})</span>
          {!expanded && activeTitle ? (
            <span className="ml-1.5 max-w-40 truncate font-normal text-ink-400 normal-case lg:hidden">· {activeTitle}</span>
          ) : null}
        </button>
        {!readOnly ? (
          <button
            type="button"
            onClick={() => onAdd(null)}
            title="Add a section at the end"
            className="rounded p-1 text-ink-400 hover:bg-ink-100 hover:text-brand-600"
          >
            <Plus className="size-4" />
          </button>
        ) : null}
      </div>

      {expanded ? (
        <nav className="min-h-0 flex-1 overflow-y-auto px-1.5 pb-3">
          {entries.length === 0 ? (
            <p className="px-2 py-4 text-[12.5px] text-ink-400">
              No sections yet.{' '}
              {!readOnly ? (
                <button type="button" onClick={() => onAdd(null)} className="font-medium text-brand-600 hover:underline">
                  Add the first one
                </button>
              ) : null}
            </p>
          ) : (
            <ul className="space-y-px">
              {entries.map((entry) => {
                const isActive = entry.section.id === activeId;
                const position = positions.get(entry.section.id) ?? { index: 0, count: 1, depth: entry.depth };
                const isFirstSibling = position.index === 0;
                const isLastSibling = position.index === position.count - 1;
                return (
                  <li key={entry.section.id}>
                    <div
                      className={clsx(
                        'group flex items-center gap-1.5 rounded-md pr-1 transition-colors',
                        isActive ? 'bg-brand-50 ring-1 ring-brand-200 ring-inset' : 'hover:bg-ink-100',
                      )}
                      style={{ paddingLeft: `${(entry.depth - 1) * 12 + 6}px` }}
                    >
                      <button
                        type="button"
                        onClick={() => {
                          onSelect(entry.section.id);
                          if (typeof window !== 'undefined' && window.innerWidth < 1024) setExpanded(false);
                        }}
                        className="flex min-w-0 flex-1 items-center gap-1.5 py-2 text-left"
                      >
                        <span className={clsx('shrink-0 font-mono text-[10.5px]', isActive ? 'text-brand-600' : 'text-ink-400')}>
                          {entry.number || '·'}
                        </span>
                        <span className={clsx('truncate text-[13px]', isActive ? 'font-semibold text-brand-800' : 'text-ink-700')}>
                          {entry.section.title || <em className="text-ink-400">Untitled</em>}
                        </span>
                        {entry.section.locked ? <Lock className="size-3 shrink-0 text-ink-300" /> : null}
                      </button>

                      {!readOnly ? (
                        <span className="flex shrink-0 items-center lg:hidden lg:group-hover:flex lg:group-focus-within:flex">
                          <IconButton
                            title={isFirstSibling ? 'Already first at this level' : 'Move up'}
                            label="Move up"
                            disabled={isFirstSibling}
                            onClick={() => onMove(entry.section.id, -1)}
                          >
                            <MoveUp className="size-3" />
                          </IconButton>
                          <IconButton
                            title={isLastSibling ? 'Already last at this level' : 'Move down'}
                            label="Move down"
                            disabled={isLastSibling}
                            onClick={() => onMove(entry.section.id, 1)}
                          >
                            <MoveDown className="size-3" />
                          </IconButton>
                          <IconButton
                            title={
                              isFirstSibling
                                ? 'Needs a section above it at the same level'
                                : position.depth >= MAX_DEPTH
                                  ? 'Already as deep as sections go'
                                  : 'Make a subsection'
                            }
                            label="Make a subsection"
                            disabled={isFirstSibling || position.depth >= MAX_DEPTH}
                            onClick={() => onIndent(entry.section.id)}
                          >
                            <Indent className="size-3" />
                          </IconButton>
                          <IconButton title="Promote" disabled={position.depth === 1} onClick={() => onOutdent(entry.section.id)}>
                            <Outdent className="size-3" />
                          </IconButton>
                          <IconButton title="Add subsection" onClick={() => onAddChild(entry.section.id)}>
                            <Plus className="size-3" />
                          </IconButton>
                          <IconButton title="Duplicate" onClick={() => onDuplicate(entry.section.id)}>
                            <Copy className="size-3" />
                          </IconButton>
                          <IconButton
                            title={entry.section.locked ? 'This section comes from the template and cannot be removed' : 'Delete section'}
                            label="Delete section"
                            disabled={entry.section.locked}
                            danger
                            onClick={() => onRemove(entry.section.id)}
                          >
                            <Trash2 className="size-3" />
                          </IconButton>
                        </span>
                      ) : null}
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </nav>
      ) : null}
    </div>
  );
}

function IconButton({
  title,
  label,
  onClick,
  disabled,
  danger,
  children,
}: {
  /** Tooltip — may explain why the action is unavailable. */
  title: string;
  /** Stable accessible name for the action itself. Defaults to the tooltip. */
  label?: string;
  onClick: () => void;
  disabled?: boolean;
  danger?: boolean;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      title={title}
      aria-label={label ?? title}
      disabled={disabled}
      onClick={onClick}
      className={clsx(
        'rounded p-1 transition-colors',
        disabled ? 'cursor-not-allowed text-ink-200' : danger ? 'text-ink-400 hover:bg-rose-50 hover:text-rose-600' : 'text-ink-400 hover:bg-white hover:text-brand-600',
      )}
    >
      {children}
    </button>
  );
}
